import React from 'react';
import { injectV2 } from '../../../injectV2';
import { Category } from '../../../../../types/api';
import { ConfigStore } from '../../../stores/ConfigStore';
import { CategoryGroup } from '../../../stores/CategoriesStore';
import { RoutingStore } from '../../../stores/RoutingStore';
import { UiLoggerStore } from '../../../stores/UiLoggerStore';
import { MegaMenu } from '../../MegaMenu/MegaMenu';
import { MegaMenuDropdownItem } from '../../MegaMenu/MegaMenuDropdownSection/MegaMenuDropdownSection';
import { ExperimentsStore } from '../../../../stores/ExperimentsStore';
import { WithTranslations, withTranslations } from '../../../../utils/withTranslations';
import { headerMenuDataHooks } from './HeaderMenu.dataHooks';

interface HeaderMenuProps extends WithTranslations {
  categoryGroup: CategoryGroup;
  configStore: ConfigStore;
  routingStore: RoutingStore;
  uiLoggerStore: UiLoggerStore;
  experimentsStore: ExperimentsStore;
  adjustWidth?: boolean;
  narrow?: boolean;
  rightTextAlign?: boolean;
  setToggleRef?: (element?: HTMLDivElement) => void;
  onItemClick?: () => void;
}

interface HeaderMenuCategory extends Category {
  customDisplayName?: string;
}

const HeaderMenuCmp: React.FC<HeaderMenuProps> = ({
  categoryGroup,
  configStore,
  routingStore,
  onItemClick,
  uiLoggerStore,
  experimentsStore,
  adjustWidth,
  narrow,
  rightTextAlign,
  setToggleRef,
  t,
}) => {
  const isFiltersExperiment = experimentsStore.is('specs.funnel.TemplatesGalleryEnableFilters', 'true');
  const { appliedFilters } = routingStore;

  const mapSubCategoryToMenuItem = (category: HeaderMenuCategory, subCategory: Category): MegaMenuDropdownItem => {
    const isActiveCategory = activeCategorySlug === category.name;
    const link =
      isFiltersExperiment && isActiveCategory
        ? routingStore.rebuildRoute({
            page: 1,
            categorySlug: category.name,
            subCategorySlug: subCategory.name,
            ...appliedFilters,
          })
        : routingStore.locationBuilders.subCategory({
            categorySlug: category.name,
            subCategorySlug: subCategory.name,
          });
    return {
      id: subCategory.name,
      label: category.customDisplayName || configStore.getMenuCategoryTitle(subCategory, t),
      link,
    };
  };

  const mapCategoryToMenuItem = (category: HeaderMenuCategory): MegaMenuDropdownItem => {
    const isActiveCategory = activeCategorySlug === category.name;
    const link =
      isFiltersExperiment && isActiveCategory
        ? routingStore.rebuildRoute({
            page: 1,
            categorySlug: category.name,
            subCategorySlug: null,
            ...appliedFilters,
          })
        : routingStore.locationBuilders.category({ categorySlug: category.name });

    return {
      id: category.name,
      label: category.customDisplayName || configStore.getMenuCategoryTitle(category, t),
      link,
      hasDelimiter: category.name === 'blank',
      items: category.subCategories.map((subCategory) => mapSubCategoryToMenuItem(category, subCategory)),
    };
  };

  const { activeCategorySlug, activeSubCategorySlug } = routingStore;
  const label = categoryGroup.displayName;
  const shortLabel = categoryGroup?.shortDisplayName || label;
  const items = categoryGroup.categories.filter((category) => !!category).map(mapCategoryToMenuItem);
  const activeCategoryName = activeSubCategorySlug || activeCategorySlug;

  const handleMenuOpen = () => uiLoggerStore.logMainCategoryMenuOpen(label);
  const handleMenuClose = () => uiLoggerStore.logMainCategoryMenuClose(label);

  return (
    <MegaMenu
      label={label}
      items={items}
      shortLabel={shortLabel}
      activeItemId={activeCategoryName}
      onItemClick={onItemClick}
      onMenuClose={handleMenuClose}
      onMenuOpen={handleMenuOpen}
      adjustWidth={adjustWidth}
      narrow={narrow}
      rightTextAlign={rightTextAlign}
      dataHook={headerMenuDataHooks.categoriesMenu({ categoryGroupName: categoryGroup.displayName })}
      setToggleRef={setToggleRef}
    />
  );
};

export const HeaderMenu = withTranslations(
  injectV2('configStore', 'routingStore', 'uiLoggerStore', 'experimentsStore')(HeaderMenuCmp),
);
